import type { GuestSession } from '@croissants/contracts/guest';
import { boundToOneShop, guestChoosesShop } from '@croissants/contracts/projections';

/** Every screen a scan can be sent to before the guest starts adding things. */
export type EntryStep = '/shops' | '/windows' | '/menu';

/**
 * What the platform is selling right now (PRD OPS-025, GST-042).
 *
 * Read off the session contract rather than restated, so the two booleans this
 * app reasons about are the two the session mints.
 */
export type OrderingModes = GuestSession['orderingModes'];

/** What a scan already knows when it lands. */
export interface JourneyState {
  /**
   * `null` while the session has not resolved yet - treated as mode A.
   *
   * Read off the session contract rather than restated, so a third storefront
   * mode is a typecheck failure here rather than a silent fall-through to the
   * mode A branch.
   */
  readonly storefrontMode: GuestSession['storefrontMode'] | null;
  /** Mode B only: the house the guest picked, if they have. */
  readonly shopId: string | null;
  /** The anchor morning, and the window on it (PRD GST-002). */
  readonly serviceDay: string | null;
  readonly windowStart: string | null;
  /**
   * What the platform sells (PRD OPS-025, GST-042). `null` while the session
   * has not resolved - treated as pre-order, which is the journey this app has
   * always had and the one the defaults ship with.
   */
  readonly orderingModes: OrderingModes | null;
}

/**
 * Whether the day-and-time picker is a STEP of the journey at all (GST-042).
 *
 * It is one exactly when pre-order is on, because that is the only mode that
 * asks a guest to choose a morning. With immediate delivery alone there is one
 * morning - today - and one soonest window on it, so a screen asking which
 * would be a checkpoint in front of a question with one answer. The picker
 * still EXISTS in that mode, reachable from "Change" and scoped to today; it is
 * simply not something the guest is sent through at all.
 *
 * Since both modes now open on the shelf, this no longer decides where a SCAN
 * lands - only whether the way OUT of a full basket passes through the picker
 * (`checkoutStep`).
 *
 * An unresolved session reads as pre-order, which is the longer path: routing
 * through a picker that turns out to be unnecessary costs a tap, and skipping
 * one that was necessary costs a refusal at the payment sheet.
 */
function pickerIsAStep(modes: OrderingModes | null): boolean {
  return modes === null || modes.preorder;
}

/**
 * Where a scan opens, per storefront mode (PRD GST-002, GST-029, GST-030).
 *
 * This is the guest journey's step order, as one pure function, because it is
 * asked in two places that MUST agree - `App.tsx`'s `Home` redirect and the
 * guard on the vitrine itself (`screens/Menu.tsx`) - and when they disagreed the
 * result was a redirect loop between them, which on a phone is a white screen
 * with no error. Stating it once means the two callers cannot drift; stating it
 * as a function rather than as JSX means it can be tested without a router.
 *
 * BOTH MODES BROWSE BEFORE THEY BOOK (founder decision, 2026-08-25). The shelf
 * is the first thing a guest sees, and the half-hour is asked on the way out of
 * the basket - the basket bar reads "Set delivery time" until a morning is set
 * (`checkoutStep`, rendered by `screens/Menu.tsx`). Mode B has worked this way
 * since GST-030; mode A now does too, and the two differ only in whether there
 * is a house to choose first.
 *
 * MODE A: straight to the shelf. Mode A had the picker FIRST, on the reasoning
 * that the vitrine is the allocated counter's and the counter cannot be known
 * until the window is. That reasoning does not survive contact with the
 * catalogue: a mode A bakery does not have a catalogue of its own. Every one of
 * them serves the platform-curated pool - `catalogue_items.bakery_org_id IS
 * NULL`, which is what `sellableBy()` selects for any mode A org - so the shelf
 * is the SAME shelf whichever bakery is allocated, and the allocation done at
 * storefront load is provisional anyway: the binding one runs at quote time,
 * with the real basket, because that is when stock and capacity mean anything.
 * The picker first was therefore a gate in front of goods that never depended
 * on it, and it made the Menu tab open a clock.
 *
 * MODE B: the house first, then its shelf, then the morning. The house is the
 * point of a branded storefront, and once it is chosen the guest wants the
 * shelf - not a second gate. Unlike mode A the catalogue here IS the house's
 * (`bakery_org_id = <that house>`), which is exactly why the house is the one
 * thing that must be settled before anything is shown.
 *
 * That is possible because `GET /g/storefront` takes `serviceDay` and
 * `windowStart` as OPTIONAL and answers a `shopId` alone with that house's
 * counter for the next service day. What the guest browses is therefore a real
 * counter, not a placeholder - and nothing on it is a quote. The morning they
 * choose afterwards is what the basket is priced and allocated for (GST-022).
 *
 * Both are RESUMABLE, which is why this takes state rather than a step number: a
 * guest returning to a code they have ordered from before lands wherever their
 * draft is incomplete, and straight on the vitrine when it is not.
 */
export function entryStep(state: JourneyState): EntryStep {
  /*
   * Only the mode with a CHOOSER has a step before the shelf. Mode B2's shop
   * is picked by the platform at the scan and named nowhere (founder,
   * 2026-09-20), so sending it to `/shops` would open a chooser the server
   * refuses.
   */
  if (state.storefrontMode !== null && guestChoosesShop(state.storefrontMode)) {
    return state.shopId === null ? '/shops' : '/menu';
  }
  /*
   * MODE B2 OPENS ON THE SHELF TOO (founder decision, 2026-09-21, replacing the
   * two-section first screen of the day before): "when Guest scans a QR code he
   * is always brought to the menu, and can see the products". There is no house
   * to choose - the platform bound one at the scan - and the delivery time is
   * asked by a POPUP over the shelf rather than by a screen in front of it
   * (`deliveryChoiceAsked`, `components/DeliveryChoice`), so nothing stands
   * between a B2 scan and the products.
   */
  // Mode A has nothing to settle before the shelf: no house to choose, and a
  // catalogue that is the same for every bakery that could serve it. The
  // morning is asked on the way out of the basket (`checkoutStep`), and with
  // immediate delivery alone it is not asked at all - there is one morning and
  // one soonest window, settled without a screen (`hooks/useImmediateWindow`).
  return '/menu';
}

/**
 * True when this state cannot render a vitrine and must be sent back a step.
 *
 * The complement of `entryStep` for the one screen that has to ask: the menu is
 * reachable directly (a bookmark, a refresh, a deep link), so it re-checks what
 * the redirect checked rather than trusting that it ran.
 */
export function vitrineBlockedBy(state: JourneyState): EntryStep | null {
  const step = entryStep(state);
  return step === '/menu' ? null : step;
}

/**
 * MODE B2: is the way out of the basket the delivery POPUP rather than a
 * screen (founder decision, 2026-09-21)?
 *
 * True while a session bound to one shop holds no delivery time. The popup
 * offers the two products today sells - "Order now" and "Schedule for later" -
 * and is what the basket bar, the basket's own button and a scan of a shut
 * bakery all open. Once a time is settled the journey is the ordinary one and
 * the way to change it is the "Change" beside the time under the address.
 *
 * ONLY WHILE IMMEDIATE DELIVERY IS SOLD (OPS-025, GST-042). The popup is a
 * choice between now and later, and "is the bakery shut" is a question about
 * an order for NOW. With that switch off there is nothing to choose between
 * and nobody to call closed - the first cut told a guest at 11:40 that a
 * bakery open until 13:00 was "currently closed", because "order now is not
 * on offer" and "the door is shut" were read as one fact. Pre-order alone is
 * the ordinary journey: the bar leads to the picker (`checkoutStep`).
 *
 * Stated here, beside `checkoutStep`, because the three callers must agree
 * with it: a bar that opens a popup on the menu and a picker from the basket is
 * the two-copies-of-one-rule bug this file exists to prevent.
 */
export function deliveryChoiceAsked(state: JourneyState): boolean {
  if (!deliveryChoiceOffered(state)) return false;
  return state.serviceDay === null || state.windowStart === null;
}

/**
 * MODE B2: does this session HAVE a delivery popup at all?
 *
 * `deliveryChoiceAsked` without the draft: a session bound to one shop, while
 * immediate delivery is sold. It is what "Change" beside the chosen time asks,
 * because a guest who has ALREADY chosen must be offered the same two sections
 * again (founder, 2026-09-21: "when pressing change ... the popup must be shown
 * again") - sending them to the picker made "now" and "later" two different
 * controls depending on whether they had answered once.
 */
export function deliveryChoiceOffered(
  state: Pick<JourneyState, 'storefrontMode' | 'orderingModes'>,
): boolean {
  if (state.storefrontMode === null || !boundToOneShop(state.storefrontMode)) return false;
  return state.orderingModes?.immediate === true;
}

/**
 * "Change", as an address: the screen it was pressed on, asked to raise the
 * popup. One-shot - the screen opens the popup and strips the parameter, so a
 * reload or a shared link never reopens it.
 */
export const CHOOSE_PARAM = 'choose';

export function deliveryChoiceHref(screen: string): string {
  return `${screen}?${CHOOSE_PARAM}=1`;
}

/**
 * The picker reached from the popup's "Schedule for later": the Soonest row is
 * not drawn, because the guest has just declined it one tap earlier.
 */
export const LATER_PARAM = 'later';

/** The picker, told where to send the guest once they have chosen. */
export function pickerThen(next: string, options: { readonly later?: boolean } = {}): string {
  const later = options.later === true ? `&${LATER_PARAM}=1` : '';
  return `/windows?next=${encodeURIComponent(next)}${later}`;
}

/**
 * The step out of a full basket, towards paying for it (PRD GST-002, GST-030).
 *
 * Since the shelf comes before the clock in BOTH modes, a guest can hold a
 * complete basket and have chosen no morning at all - they never passed a
 * picker. So the way out asks for one, and only then reviews: the morning is
 * the last missing fact, not a fault, and the screen that used to answer it
 * with a red "an error occurred" panel is what this exists to prevent.
 * `?next=` carries the guest on to the review step, because asking to check out
 * is what got them here.
 *
 * This is now mode A's ONLY route to the picker on the way in, which is what
 * makes the reversal safe: the morning is settled before the quote either way,
 * and the quote is where the basket is actually priced and allocated.
 *
 * Stated ONCE, for the same reason `entryStep` is: three screens ask it - the
 * vitrine's basket bar, the basket's own button and the review step's guard -
 * and three hand-written copies of one rule is how the first two came to send a
 * guest somewhere the third refused them.
 */
export function checkoutStep(state: JourneyState, review = '/checkout'): string {
  // With immediate delivery alone the morning is never the missing fact, so the
  // bar reads "View basket" from the first item onwards (GST-042).
  if (!pickerIsAStep(state.orderingModes)) return review;
  const needsMorning = state.serviceDay === null || state.windowStart === null;
  return needsMorning ? pickerThen(review) : review;
}

/**
 * Where the Menu tab leads (founder, 2026-09-18).
 *
 * In mode B the tab is two presses deep, and the SCREEN the guest is on decides
 * which one this is. From the basket or the orders it opens the chosen house's
 * counter, as it always did. Pressed again ON that counter it goes one further
 * back, to the chooser - the same place the arrow beside the house's name
 * leads, so the two ways back cannot disagree. Mode A has no chooser, so its
 * tab has one answer, and so does mode B2: it has no chooser either, and
 * `/shops` is refused for a B2 session outright, so routing the tab there would
 * open a screen the server will not answer.
 */
export function menuTabHref(
  storefrontMode: JourneyState['storefrontMode'],
  location: string,
): '/menu' | '/shops' {
  return storefrontMode !== null && guestChoosesShop(storefrontMode) && location.startsWith('/menu')
    ? '/shops'
    : '/menu';
}
